import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import React, { useState } from 'react'
import type { FollowUpSentActivityDetails } from '@paintscout/api'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import classnames from 'classnames'
import striptags from 'striptags'
import Highlight from '@paintscout/ui/components/Highlight'
import Typography from '@paintscout/ui/components/Typography'
import HtmlContent from '@paintscout/ui/components/HtmlContent'
import Collapse from '@paintscout/ui/components/Collapse'
import IconButton from '@paintscout/ui/components/IconButton'
import type { ActivityTypeComponentProps } from '../Activity'
import ActivityContent from '../ActivityContent'
import ActivityFooter from '../ActivityFooter'
import ActivityTitle from '../ActivityTitle'
import QuoteActivitySubtitle from '../QuoteActivitySubtitle'
import ActivityDot from '../ActivityDot'

const useStyles = makeStyles<Theme, ActivityTypeComponentProps<FollowUpSentActivityDetails>>((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: 'fit-content',
    '& > div': {
      width: '100%'
    }
  },
  email: {
    maxHeight: 150,
    overflowY: 'auto'
  },
  emailTo: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.paintscout.palette.brand.Neutral.P60
  },
  emailSubject: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  emailContent: {
    color: theme.palette.text.secondary,
    textAlign: 'left',
    marginTop: theme.spacing(1)
  },
  spacer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  collapseText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer'
  }
}))

export default function FollowUpSentActivity(props: ActivityTypeComponentProps<FollowUpSentActivityDetails>) {
  const { activity, showContent, showDocTypeDetails } = props
  const classes = useStyles(props)
  const { email, text, smsTo, activityType } = activity.details
  const [collapsed, setCollapsed] = useState(true)

  const emailTemplate = activity.details?.events?.[0] ?? ''

  const getTitle = () => {
    if (activityType === 'event-email-sent') {
      if (!emailTemplate) {
        return 'Event email sent'
      }
      if (emailTemplate.includes('event-reminder')) {
        return 'Event reminder email'
      }
      if (emailTemplate === 'event-cancelled') {
        return 'Event cancellation email'
      }
      return `${emailTemplate.replace('-', ' ').charAt(0).toUpperCase()}${emailTemplate.replace('-', ' ').slice(1)} email`
    }
    if (activityType === 'payment-received-sent') {
      return 'Payment received email'
    }
    if (email && text) {
      return 'Follow-up email & text'
    }
    if (email) {
      return 'Follow-up email'
    }
    return 'Follow-up text'
  }

  const getReminderText = () => {
    const match = emailTemplate.match(/(\d+-(hour|day))/)
    return match ? ` (${match[1].replace('-', ' ')}) ` : ''
  }

  const handleCollapseChange = (e, isOpen: boolean) => {
    e.preventDefault()
    e.stopPropagation()
    setCollapsed(isOpen)
  }

  return (
    <>
      <ActivityTitle>
        {activityType !== 'event-email-sent' && <ActivityDot activity={activity} />}
        <Highlight>{getTitle()}</Highlight>
        {getReminderText()} {email && text ? 'were' : 'was'} <Highlight>sent</Highlight>
      </ActivityTitle>
      {showContent && (
        <ActivityContent className={classes.content}>
          <Collapse show={collapsed}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.collapseText}
              style={{ height: 16, width: 'fit-content' }}
              onClick={(e) => handleCollapseChange(e, false)}
            >
              View message{email && text ? 's' : ''}
              <IconButton size="small" style={{ marginLeft: 8 }}>
                <KeyboardArrowDownIcon />
              </IconButton>
            </Typography>
          </Collapse>
          <Collapse show={!collapsed}>
            <>
              {email && (
                <div className={classes.email}>
                  <Typography
                    variant="body1"
                    component="h5"
                    className={classnames(classes.emailSubject, classes.collapseText)}
                    onClick={(e) => handleCollapseChange(e, true)}
                  >
                    {striptags(email.subject)}
                    <IconButton size="small">
                      <KeyboardArrowUpIcon />
                    </IconButton>
                  </Typography>
                  <Typography variant="subtitle2" component="h4" className={classes.emailTo}>
                    to {email.to.join(', ')}
                  </Typography>
                  <HtmlContent content={email.content} classes={{ root: classes.emailContent }} />
                </div>
              )}
              {email && text && <div className={classes.spacer} />}
              {text && (
                <div className={classes.email}>
                  <Typography
                    variant="subtitle2"
                    component="h4"
                    className={classnames(classes.emailTo, classes.collapseText)}
                    style={{ height: 16 }}
                  >
                    to {smsTo}
                    {!email && (
                      <IconButton size="small" onClick={(e) => handleCollapseChange(e, true)}>
                        <KeyboardArrowUpIcon />
                      </IconButton>
                    )}
                  </Typography>

                  <HtmlContent content={text} classes={{ root: classes.emailContent }} />
                </div>
              )}
            </>
          </Collapse>
        </ActivityContent>
      )}
      <QuoteActivitySubtitle activity={activity} showDocTypeDetails={showDocTypeDetails} />
      <ActivityFooter timestamp={activity.details.timestamp} />
    </>
  )
}
